import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  Loader2, Lock, ShieldAlert, ExternalLink, CreditCard, Info, Trash2, RefreshCw,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useDocumentMeta } from '../utils/useDocumentMeta';
import { startPayment, adminSmokeTestStatus, adminSmokeTestCleanupPayments } from '../services/api';

const ADMIN_KEY_STORAGE = 'orosz_admin_key_v1';
const SMOKE_PROGRAM_ID = 'smoke-test-1ft';

const LoginScreen = ({ onAuth }) => {
  const [key, setKey] = useState('');
  return (
    <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center p-6">
      <form
        onSubmit={(e) => { e.preventDefault(); if (key.trim()) { localStorage.setItem(ADMIN_KEY_STORAGE, key.trim()); onAuth(key.trim()); } }}
        data-testid="admin-smoke-login"
        className="w-full max-w-md bg-gray-800/80 rounded-2xl p-8 border border-gray-700 shadow-2xl"
      >
        <div className="flex justify-center mb-6">
          <div className="w-14 h-14 rounded-full bg-red-600/20 flex items-center justify-center">
            <ShieldAlert className="text-red-400" size={28} />
          </div>
        </div>
        <h1 className="text-2xl font-bold text-center mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
          Barion Smoke Test
        </h1>
        <Input
          data-testid="admin-smoke-key"
          type="password"
          value={key}
          onChange={(e) => setKey(e.target.value)}
          placeholder="ADMIN_API_KEY"
          className="bg-gray-900 border-gray-700 text-white mb-4"
          autoFocus
        />
        <Button data-testid="admin-smoke-login-btn" type="submit" disabled={!key.trim()} className="w-full bg-yellow-600 hover:bg-yellow-700 text-gray-900 font-bold">
          <Lock className="mr-2" size={16} /> Belépés
        </Button>
      </form>
    </div>
  );
};

const AdminSmokeTestPage = () => {
  useDocumentMeta({ title: 'Barion Smoke Test | Admin' });
  const [adminKey, setAdminKey] = useState(() => localStorage.getItem(ADMIN_KEY_STORAGE) || '');
  const [busy, setBusy] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [result, setResult] = useState(null);
  const [status, setStatus] = useState(null);

  const loadStatus = useCallback(async (key) => {
    try {
      const s = await adminSmokeTestStatus(key || adminKey);
      setStatus(s);
    } catch (err) {
      // Silent fail — status is a nice-to-have
    }
  }, [adminKey]);

  useEffect(() => { if (adminKey) loadStatus(adminKey); }, [adminKey, loadStatus]);

  // Prefilled billing — coach's own real data, editable if needed.
  const [billing, setBilling] = useState({
    name:    'Orosz Gábor',
    email:   'info@oroszcoaching.hu',
    address: 'Dankó Pista utca 27. 5/1.',
    zip:     '3530',
    city:    'Miskolc',
    country: 'HU',
  });

  if (!adminKey) return <LoginScreen onAuth={setAdminKey} />;

  const run = async () => {
    setBusy(true);
    setResult(null);
    try {
      const res = await startPayment({
        program_id: SMOKE_PROGRAM_ID,
        tier_index: 0,
        billing,
        source:     'admin-smoke-test',
      });
      setResult(res);
      // Redirect to Barion gateway after 1s so the coach sees the response
      setTimeout(() => {
        if (res?.gateway_url) window.location.href = res.gateway_url;
      }, 1200);
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message;
      toast.error(`Sikertelen indítás: ${detail}`);
    } finally { setBusy(false); }
  };

  const runCleanup = async () => {
    if (!window.confirm('Biztos törölni szeretnéd az összes smoke test payment rekordot? A smoke test program megmarad, csak a fizetési rekordok tűnnek el.')) return;
    setCleaning(true);
    try {
      const res = await adminSmokeTestCleanupPayments(adminKey);
      toast.success(`Törölve: ${res.payments_deleted} smoke test payment rekord.`);
      await loadStatus();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Cleanup sikertelen.');
    } finally { setCleaning(false); }
  };

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <header className="border-b border-gray-800 bg-gray-900/80 backdrop-blur">
        <div className="container mx-auto px-6 py-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
            <CreditCard className="text-red-400" size={20} />
          </div>
          <div>
            <h1 className="text-lg font-bold" style={{ fontFamily: 'Montserrat, sans-serif' }}>Barion Smoke Test — 100 Ft élő</h1>
            <p className="text-xs text-gray-400">Belső technikai teszt. Ne futtasd, ha nem TE vagy az edző.</p>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-6 py-10 max-w-2xl space-y-6">
        <div className="rounded-2xl border border-red-500/30 bg-red-950/20 p-5">
          <div className="flex items-start gap-3">
            <Info size={20} className="text-red-400 shrink-0 mt-0.5" />
            <div className="text-sm text-red-100/90 space-y-2">
              <p className="font-bold">Ez egy VALÓDI Barion tranzakció.</p>
              <ul className="list-disc pl-5 space-y-1 text-red-100/70">
                <li>100 Ft-ot a bankkártyádról le fog vonni és jóváír a Barion egyenlegedre (Barion kb. 50 Ft alatt nem engedi a kártyás tranzakciót).</li>
                <li>Program: <code className="text-red-300">smoke-test-1ft</code> — nem publikus, csak közvetlen URL-en érhető el.</li>
                <li>NEM készül Számlázz.hu számla, NEM megy visszaigazoló email, NEM aktiválódik bérlet.</li>
                <li>A sikeres tranzakció után a <code className="text-red-300">payments</code> collectionban keletkezik egy rekord — kézzel törölhető.</li>
              </ul>
            </div>
          </div>
        </div>

        {status && (
          <div data-testid="smoke-status-card" className="rounded-2xl border border-gray-800 bg-gray-900/60 p-5 flex items-center justify-between gap-4 flex-wrap">
            <div className="text-sm">
              <div className="text-gray-400 text-xs uppercase tracking-widest font-bold mb-1">Smoke test státusz</div>
              <div className="text-white">
                Program: <span className={status.program ? 'text-emerald-300' : 'text-red-300'}>{status.program ? 'aktív' : 'hiányzik'}</span>
                <span className="text-gray-600 mx-2">·</span>
                Payment rekordok: <span className="font-bold text-white">{status.payments_count || 0}</span>
                {status.latest_payment?.status && (
                  <>
                    <span className="text-gray-600 mx-2">·</span>
                    Utolsó: <span className="text-gray-300">{status.latest_payment.status}</span>
                  </>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button data-testid="smoke-status-refresh" variant="ghost" size="sm" onClick={() => loadStatus()} className="text-gray-400 hover:text-white">
                <RefreshCw size={13} className="mr-1.5" /> Frissítés
              </Button>
              <Button
                data-testid="smoke-cleanup-btn"
                variant="outline"
                size="sm"
                onClick={runCleanup}
                disabled={cleaning || !status.payments_count}
                className="border-red-500/40 text-red-300 hover:bg-red-500/10 disabled:opacity-40"
              >
                {cleaning ? (
                  <><Loader2 className="animate-spin mr-1.5" size={13} /> Törlés…</>
                ) : (
                  <><Trash2 size={13} className="mr-1.5" /> Payment rekordok törlése ({status.payments_count || 0})</>
                )}
              </Button>
            </div>
          </div>
        )}

        <section className="rounded-2xl border border-gray-800 bg-gray-900/60 p-5 space-y-3">
          <h2 className="text-sm font-bold text-gray-300 mb-2">Számlázási adatok (Barion tranzakcióhoz)</h2>
          {['name','email','address','zip','city'].map((k) => (
            <div key={k}>
              <label className="text-[11px] uppercase tracking-widest text-gray-500 font-bold block mb-1">{k}</label>
              <Input
                data-testid={`smoke-billing-${k}`}
                value={billing[k]}
                onChange={(e) => setBilling((b) => ({ ...b, [k]: e.target.value }))}
                className="bg-gray-950 border-gray-700 text-white"
                disabled={busy}
              />
            </div>
          ))}
        </section>

        <Button
          data-testid="smoke-run-btn"
          onClick={run}
          disabled={busy}
          className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-6 text-base"
        >
          {busy ? (
            <><Loader2 className="mr-2 animate-spin" size={18} /> Barion tranzakció indítása…</>
          ) : (
            <><CreditCard className="mr-2" size={18} /> Indíts 100 Ft-os élő Barion teszt tranzakciót</>
          )}
        </Button>

        {result && (
          <div className="rounded-xl border border-emerald-500/30 bg-emerald-950/30 p-5 space-y-2">
            <div className="text-emerald-300 font-bold text-sm">Tranzakció létrehozva — átirányítás Barion-ra…</div>
            <div className="text-xs text-emerald-100/80 font-mono space-y-1">
              <div>order_id: {result.order_id}</div>
              <div>barion_payment_id: {result.barion_payment_id}</div>
              <div>amount: {result.amount_huf} Ft · environment: {result.environment}</div>
            </div>
            {result.gateway_url && (
              <a
                href={result.gateway_url}
                className="inline-flex items-center gap-1.5 text-emerald-200 font-semibold text-sm hover:text-white"
                data-testid="smoke-gateway-link"
              >
                Ha nem indul automatikusan az átirányítás, kattints ide <ExternalLink size={12} />
              </a>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminSmokeTestPage;
